import type { FC } from 'react';

import { SECTION_IDS } from '@/features/home/constants/home.constants';
import { SITE_CONTAINER } from '@/features/home/constants/homeStyles.constants';
import type { RentCategory, RentNote, SectionIntro } from '@/types/home.types';

interface RentCategoriesSectionProps {
  intro: SectionIntro;
  categories: RentCategory[];
  notes: RentNote[];
}

const RentCategoriesSection: FC<RentCategoriesSectionProps> = ({ intro, categories, notes }) => (
  <section id={SECTION_IDS.rent} className="scroll-mt-20 bg-site-bg">
    <div className={`${SITE_CONTAINER} pb-14 md:pb-[72px]`}>
      <span className="block font-site text-[13px] tracking-[0.08em] text-site-accent-deep uppercase">
        {intro.eyebrow}
      </span>

      <h2 className="mt-4 max-w-[24ch] font-site text-[clamp(28px,3vw,40px)] leading-[1.1] font-extrabold tracking-[-0.02em]">
        {intro.title}
      </h2>

      <div className="mt-8 grid grid-cols-[repeat(auto-fit,minmax(min(100%,240px),1fr))] gap-0.5 border-2 border-site-ink bg-site-ink">
        {categories.map((category) => (
          <div
            key={category.title}
            className={`flex flex-col gap-2 px-[22px] pt-[22px] pb-[26px] ${
              category.highlighted
                ? 'justify-between bg-site-accent text-white'
                : 'bg-site-bg transition-colors hover:bg-site-accent-soft'
            }`}
          >
            <div className="font-site text-[19px] font-extrabold">{category.title}</div>
            <div
              className={`font-site text-[15px] leading-[1.45] ${
                category.highlighted ? 'text-white' : 'text-site-ink/80'
              }`}
            >
              {category.body}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10 grid grid-cols-[repeat(auto-fit,minmax(min(100%,260px),1fr))] gap-8 md:mt-12">
        {notes.map((note) => (
          <div key={note.title} className="border-t-2 border-site-ink pt-4">
            <div className="font-site text-[18px] font-extrabold">{note.title}</div>
            <p className="mt-1.5 font-site text-[15px] leading-[1.5] text-site-ink/80">
              {note.body}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default RentCategoriesSection;
